import { useRef, useState, useEffect } from 'react';
import { Formik } from "formik";
import * as Yup from "yup";
import { Form as BootstrapForm, Button } from "react-bootstrap";
import emailjs from '@emailjs/browser';
import './RequestEstimateForm.css';

const RECAPTCHA_SITE_KEY = '6LerTakqAAAAAPAmdH55gVV8IE7egXg6_W0t49IJ'

const RequestEstimateForm = ({ onSuccess }) => {
    const formRef = useRef();
    const [submitError, setSubmitError] = useState('');
    const [isRecaptchaLoaded, setIsRecaptchaLoaded] = useState(false);
    const widgetId = useRef(null);
    const recaptchaToken = useRef(null);

    useEffect(() => {
        const loadRecaptcha = () => {
            if (!window.grecaptcha) return;

            window.grecaptcha.ready(() => {
                try {
                    if (!widgetId.current) {
                        widgetId.current = window.grecaptcha.render('submit-button', {
                            sitekey: RECAPTCHA_SITE_KEY,
                            size: 'invisible',
                            callback: (token) => {
                                console.log('reCAPTCHA callback executed with token:', token);
                                recaptchaToken.current = token; //save the token
                                formRef.current.submitForm(); //submit the form
                            },
                        });
                        setIsRecaptchaLoaded(true);
                    }
                } catch (error) {
                    console.error('reCAPTCHA render error:', error);
                }
            });
        };

        // Load the reCAPTCHA script
        if (!document.querySelector('script[src*="recaptcha"]')) {
            const script = document.createElement('script');
            script.src = `https://www.google.com/recaptcha/api.js?render=explicit`;
            script.async = true;
            script.defer = true;
            script.onload = loadRecaptcha;
            document.body.appendChild(script);
        } else {
            loadRecaptcha();
        }

        return () => {
            if (widgetId.current !== null) {
                window.grecaptcha.reset(widgetId.current);
            }
        };
    }, []);

    const handleSubmit = async (values, { resetForm, setSubmitting }) => {
        try {
            setSubmitError('');

            const token = recaptchaToken.current;

            if (!token) {
                throw new Error('reCAPTCHA token not received.');
            }

            // Send email using EmailJS with the reCAPTCHA token
            await emailjs.send(
                'service_9djkchf',
                'template_neuri85',
                {
                    ...values,
                    'g-recaptcha-response': token, // Use the token here
                },
                {
                    publicKey: 'mVGzLwtt-Azidd2RU',
                }
            );

            console.log('Email sent successfully!');
            if (onSuccess) onSuccess();
            resetForm();
            window.grecaptcha?.reset(widgetId.current);

        } catch (error) {
            console.error("Error during form submission:", error);
            setSubmitError(error.message || 'Failed to submit form. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };



    // Validation schema using Yup
    const validationSchema = Yup.object({
        form_name: Yup.string().required("Please enter your name"),
        form_email: Yup.string().email("This is not a valid email.").required("Please enter your email."),
        form_phone: Yup.string()
            .matches(/^\d{10}$/, "Phone number must be 10 digits.")
            .required("Please enter your phone number.")
    });

    // Initial values for the form fields
    const initialValues = {
        form_name: "",
        form_email: "",
        form_phone: "",
        form_message: "",
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            innerRef={formRef}
        >
            {({ handleSubmit, handleChange, values, touched, errors, isSubmitting }) => (
                <BootstrapForm id="submit-form" ref={formRef} noValidate onSubmit={handleSubmit}>
                    {/* Hidden input for reCAPTCHA response */}
                    <input type="hidden" id="g-recaptcha-response" name="g-recaptcha-response" />


                    {/* Name Field */}
                    <BootstrapForm.Group controlId="name">
                        <BootstrapForm.Control
                            type="text"
                            placeholder="Name"
                            name="form_name"
                            value={values.form_name}
                            onChange={handleChange}
                            isInvalid={touched.form_name && !!errors.form_name}
                        />
                        <BootstrapForm.Control.Feedback type="invalid">
                            {errors.form_name}
                        </BootstrapForm.Control.Feedback>
                    </BootstrapForm.Group>

                    {/* Email Field */}
                    <BootstrapForm.Group controlId="email" className="mt-3">
                        <BootstrapForm.Control
                            type="email"
                            placeholder="Email"
                            name="form_email"
                            value={values.form_email}
                            onChange={handleChange}
                            isInvalid={touched.form_email && !!errors.form_email}
                        />
                        <BootstrapForm.Control.Feedback type="invalid">
                            {errors.form_email}
                        </BootstrapForm.Control.Feedback>
                    </BootstrapForm.Group>

                    {/* Phone Field */}
                    <BootstrapForm.Group controlId="phone" className="mt-3">
                        <BootstrapForm.Control
                            type="text"
                            placeholder="Phone Number"
                            name="form_phone"
                            value={values.form_phone}
                            onChange={handleChange}
                            isInvalid={touched.form_phone && !!errors.form_phone}
                        />
                        <BootstrapForm.Control.Feedback type="invalid">
                            {errors.form_phone}
                        </BootstrapForm.Control.Feedback>
                    </BootstrapForm.Group>

                    {/* Message Field */}
                    <BootstrapForm.Group controlId="message" className="mt-3">
                        <BootstrapForm.Control
                            as="textarea"
                            rows={6}
                            placeholder="How can we help?"
                            name="form_message"
                            value={values.form_message}
                            onChange={handleChange}
                            isInvalid={touched.form_message && !!errors.form_message}
                        />
                        <BootstrapForm.Control.Feedback type="invalid">
                            {errors.form_message}
                        </BootstrapForm.Control.Feedback>
                    </BootstrapForm.Group>

                    {/* Error Message */}
                    {submitError && (
                        <div className="text-danger mt-3">
                            {submitError}
                        </div>
                    )}

                    {/* Submit Button */}
                    <Button
                        id="submit-button"
                        type="submit"
                        disabled={isSubmitting || !isRecaptchaLoaded}
                        className="mt-4"
                    >
                        {isSubmitting ? 'Submitting...' : 'Submit'}
                    </Button>
                </BootstrapForm>
            )}
        </Formik>
    );
};

export default RequestEstimateForm;